<template>
    <validation-observer ref="form" v-slot="{ setFieldValue, meta, errors }" as="span">
        <modal
            :id="modalId"
            :title="getTitle()"
            @show="onShow"
            :static="isStatic"
            :scrollable="false"
            class="payment-modal"
            :ok-disabled="!meta.valid || busy"
            :ok-title="isRecord ? 'Record' : 'Process'"
            @ok.prevent="isUpdate ? updateBill() : addBill()"
        >
            <busy-modal :show="busy"></busy-modal>
            <b-form @submit.prevent="">
                <b-row>
                    <b-col>
                        <validation-provider name="sumPaid" rules="validFloat|required" >
                            <form-input
                                v-if="isPayment"
                                floating-label="Amount"
                                v-model="sumPaid"
                                :rightButton="{
                                    title: '',
                                    text: 'Amount to Confirm',
                                    variant: 'outline-primary',
                                    toggle: false,
                                }"
                                @handle-right-button-click="setAmountToConfirm()"
                                :rightButton2="{
                                    title: '',
                                    text: 'Amount to Balance',
                                    variant: 'outline-primary',
                                    toggle: false,
                                }"
                                @handle-right-button-2-click="setAmountToBalance()"
                                :degrade-on-small="true"
                                drop-down-text="Select Amount"
                                :error="errors.sumPaid"
                                @input="(val) => setFieldValue('sumPaid', val)"
                            />
                            <form-input
                                v-if="isRefund"
                                @input="(val) => setFieldValue('sumPaid', val)"
                                floating-label="Amount"
                                v-model="sumPaid"
                                :error="errors.sumPaid"
                            />
                            <form-input
                                v-if="isPreAuth"
                                floating-label="Pre-Auth Amount"
                                v-model="sumPaid"
                                :rightButton="{
                                    title: '',
                                    text: 'Auto Fill',
                                    variant: 'outline-primary',
                                    toggle: false,
                                }"
                                @handle-right-button-click="preAuthAutoFill()"
                                :error="errors.sumPaid"
                                @input="(val) => setFieldValue('sumPaid', val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="(isRecord || isUpdate) && !isPreAuth">
                    <b-col>
                        <form-datepicker
                            v-model="date"
                            floating-label="Date"
                            class="mt-2"
                            :hide-header="true"
                            :display-format="userUnicodeDateFormat"
                        ></form-datepicker>
                    </b-col>
                </b-row>
                <b-row v-if="!(isPreAuth && isRecord) && !(isPreAuth && isUpdate)">
                    <b-col>
                        <validation-provider name="payment_method" rules="required">
                            <form-select
                                floating-label="Payment Method"
                                v-model="paymentMethod"
                                :options="paymentMethodOptions"
                                :error="errors.payment_method"
                                :disabled="isProcess || isPreAuth"
                                @input="(val) => setFieldValue('payment_method', val)"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="paymentMethod in EXTRA_DATA_LABELS">
                    <b-col>
                        <form-input
                            v-if="paymentMethod != 14"
                            :floating-label="EXTRA_DATA_LABELS[paymentMethod]"
                            v-model="extraData"
                        />
                        <form-input
                            v-if="isGuestCredit"
                            floating-label="Code"
                            v-model="guestCreditPlaceholder"
                            :readonly="true"
                        />
                    </b-col>
                </b-row>
                <b-row v-if="!isGuestCredit && (isRecord || isUpdate) && !isPreAuth">
                    <b-col>
                        <form-textarea
                            floating-label="Notes"
                            v-model="notes"
                        ></form-textarea>
                    </b-col>
                </b-row>
                <b-row v-if="isGuestCredit && !isUpdate">
                    <b-col>
                        <form-select
                            floating-label="Send"
                            v-model="sendToGuest"
                            :options="sendToGuestOptions"
                        ></form-select>
                    </b-col>
                </b-row>
                <span v-if="isProcess && isCC">
                    <b-row>
                        <b-col>
                            <form-input
                                floating-label="Card Type"
                                v-model="reservation.process_payment_data.field1"
                                :disabled="true"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <form-input
                                floating-label="Expiration Date"
                                v-model="reservation.process_payment_data.field2"
                                :disabled="true"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <form-input
                                floating-label="Card Number"
                                v-model="reservation.process_payment_data.field3"
                                :disabled="true"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <form-input
                                floating-label="Card CVV2"
                                v-model="reservation.process_payment_data.field4"
                                :disabled="true"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <form-input
                                floating-label="Card Holder"
                                v-model="reservation.process_payment_data.field5"
                                :disabled="true"
                            />
                        </b-col>
                    </b-row>
                </span>
                <span v-if="isProcess && isECheck">
                    <b-row>
                        <b-col>
                            <form-input
                                floating-label="Routing Number"
                                v-model="reservation.process_payment_data.field1"
                                :disabled="true"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <form-input
                                floating-label="Account Number"
                                v-model="reservation.process_payment_data.field2"
                                :disabled="true"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <form-input
                                floating-label="Account Name"
                                v-model="reservation.process_payment_data.field3"
                                :disabled="true"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <form-input
                                floating-label="Bank Name"
                                v-model="reservation.process_payment_data.field4"
                                :disabled="true"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <form-input
                                floating-label="Account Type"
                                v-model="reservation.process_payment_data.field5"
                                :disabled="true"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <form-input
                                floating-label="Check Type"
                                v-model="reservation.process_payment_data.field6"
                                :disabled="true"
                            />
                        </b-col>
                    </b-row>
                </span>
                <b-row v-if="isRefund">
                    <b-col class="text-danger">
                        Important! NO refunds are processed using this function. The process of refunding to a credit
                        card can only be done manually at the payment gateway. This function will only record the refund
                        to the invoice.
                    </b-col>
                </b-row>
                <b-row v-if="isPreAuth && isProcess">
                    <b-col class="text-danger">
                        A credit card Pre-Authorization is a temporary 3 to 7 day hold on funds. This process does NOT
                        collect funds. Use the Process Payment function to charge the credit card of a guest.
                    </b-col>
                </b-row>
                <b-row v-if="isPreAuth && isRecord">
                    <b-col class="text-danger"> Pre-Auth will be recorded but NOT processed. </b-col>
                </b-row>
                <b-row v-if="isAutomatedPaymentsEnabled">
                    <b-col>
                        <small class="text-danger"> Warning: Automated Payments Enabled! </small>
                    </b-col>
                </b-row>
            </b-form>
        </modal>
    </validation-observer>
</template>

<script>
    import API from '../../api';
    import isEmpty from 'lodash/isEmpty';
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import parseISO from 'date-fns/parseISO';
    import FormInput from '../generic/form-input/form-input.vue';
    import FormSelect from '../generic/form-select/form-select.vue';
    import FormDatepicker from '../generic/form-datepicker/form-datepicker.vue';
    import FormTextarea from '../generic/form-textarea/form-textarea.vue';
    import Modal from '../generic/modal/modal.vue';
    import BusyModal from '../generic/busy-modal/busy-modal.vue';
    import { invoiceMixin } from '../reservation/mixins/invoice-mixin';
    import { BILL_TYPE_MAPPING } from '../../constants';
    import { formatResponseError } from '../../utils/api';
    import {
        RESERVATION_INVOICE_PAYMENT_METHOD_CHOICES_ENDPOINT,
        RESERVATION_INVOICE_ADD_BILL_ENDPOINT,
        RESERVATION_INVOICE_PROCESS_BILL_ENDPOINT,
        GUEST_RESERVATION_UPDATE_BILL_ENDPOINT,
    } from '../../endpoints';

    export default {
        name: 'PaymentModal',
        components: {
            FormTextarea,
            FormDatepicker,
            FormInput,
            FormSelect,
            Modal,
            BusyModal,
        },
        mixins: [invoiceMixin],
        props: {
            initialValues: {
                type: Object,
                default: () => ({}),
            },
            isStatic: {
                type: Boolean,
                default: true,
            },
            onUpdate: {
                type: Function,
                default: () => {},
            },
            bill: {
                type: Object,
                default: () => ({}),
            },
            modalId: String,
            mode: String,
            type: {
                type: Number,
                default: 0,
            },
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            ...mapGetters('paymentGateway', ['automatedPaymentSettings']),
            isAutomatedPaymentsEnabled() {
                return this.automatedPaymentSettings.enabled;
            },
            isUpdate() {
                return this.mode == 'UPDATE';
            },
            isProcess() {
                return this.mode == 'PROCESS';
            },
            isRecord() {
                return this.mode == 'RECORD';
            },
            isGuestCredit() {
                return this.paymentMethod == 14;
            },
            isPayment() {
                return this.type == 0;
            },
            isRefund() {
                return this.type == 1;
            },
            isPreAuth() {
                return this.type == 2;
            },
            isCC() {
                return this.reservation.process_payment_data?.profile_type == 0 || false;
            },
            isECheck() {
                return this.reservation.process_payment_data?.profile_type == 1 || false;
            },
        },
        created() {
            this.reservation = this.initialValues;
            this.EXTRA_DATA_LABELS = {
                1: 'Check #',
                2: 'Certificate #',
                14: 'Code',
            };
        },
        data() {
            return {
                sumPaid: null,
                date: null,
                paymentMethod: null,
                notes: null,
                extraData: null,
                paymentMethodOptions: [],
                paymentPaymentMethodOptions: [],
                refundPaymentMethodOptions: [],
                processPaymentMethodOptions: [],
                sendToGuest: true,
                sendToGuestOptions: [
                    { text: 'Email credit code to guest', value: true },
                    { text: 'Do not email credit code to guest', value: false },
                ],
                guestCreditPlaceholder: null,
            };
        },
        methods: {
            setAmountToConfirm() {
                this.sumPaid = this.reservation.remainders.remain_to_deposit;
                this.$refs.form.setFieldValue('sumPaid', this.sumPaid);
            },
            setAmountToBalance() {
                this.sumPaid = this.reservation.remainders.remain_to_whole;
                this.$refs.form.setFieldValue('sumPaid', this.sumPaid);
            },
            preAuthAutoFill() {
                this.sumPaid = this.reservation.remainders.preauth_amount;
                this.$refs.form.setFieldValue('sumPaid', this.sumPaid);
            },
            async onShow() {
                this.sumPaid = this?.bill?.sum_paid || null;
                this.date = this?.bill?.date || null;
                this.paymentMethod = this?.bill?.payment_method || null;
                this.guestCreditPlaceholder = this?.bill?.code || 'Guest Credit code will be auto generated.';
                this.notes = this?.bill?.notes || null;
                this.extraData = this?.bill?.extra_data || null;
                this.sendToGuest = null;

                await this.fetchData();

                if (this.isRecord || this.isUpdate) {
                    if ((this.isPayment || this.isPreAuth) && !this.isUpdate) {
                        this.paymentMethodOptions = this.paymentPaymentMethodOptions;
                    } else if (this.isRefund || this.isUpdate) {
                        this.paymentMethodOptions = this.refundPaymentMethodOptions;
                    }
                    if (this.paymentMethodOptions.length > 0) {
                        if (this.isPreAuth) {
                            this.paymentMethod = this.paymentMethodOptions.find((obj) => {
                                return obj.value === 6;
                            }).value;
                        } else if (this.isUpdate) {
                            this.paymentMethod = this.paymentMethodOptions.find(
                                (p) => p.value === this.paymentMethod
                            ).value;
                        } else {
                            this.paymentMethod = this.paymentMethodOptions[1].value;
                        }
                    }
                } else if (this.isProcess) {
                    this.paymentMethodOptions = this.processPaymentMethodOptions;
                    if (this.paymentMethodOptions.length > 0) {
                        if (this.isCC) {
                            this.paymentMethod = this.paymentMethodOptions.find((obj) => {
                                return obj.value === 4;
                            }).value;
                        } else if (this.isECheck) {
                            this.paymentMethod = this.paymentMethodOptions.find((obj) => {
                                return obj.value === 9;
                            }).value;
                        }
                    }
                }
                this.$refs.form.setValues({
                    sumPaid: this.sumPaid,
                    date: this.date,
                    payment_method: this.paymentMethod,
                    extra_data: this.extraData,
                    notes: this.notes,
                });
            },
            async fetchData() {
                try {
                    this.busy = true;
                    const response = await API.get(
                        RESERVATION_INVOICE_PAYMENT_METHOD_CHOICES_ENDPOINT(this.reservation.id)
                    );
                    this.paymentPaymentMethodOptions = response.data.payment;
                    this.refundPaymentMethodOptions = response.data.refund;
                    this.processPaymentMethodOptions = response.data.process;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch data' });
                } finally {
                    this.busy = false;
                }
            },
            getTitle() {
                return this.capitalize(this.mode) + ' ' + BILL_TYPE_MAPPING[this.type];
            },
            capitalize: function (value) {
                if (!value) return '';
                value = value.toString().toLowerCase();
                return value.charAt(0).toUpperCase() + value.slice(1);
            },
            async addBill() {
                try {
                    this.busy = true;

                    let ENDPOINT = RESERVATION_INVOICE_ADD_BILL_ENDPOINT;
                    if (this.isProcess) {
                        ENDPOINT = RESERVATION_INVOICE_PROCESS_BILL_ENDPOINT;
                    }

                    let data = {
                        sum_paid: this.sumPaid,
                        bill_type: this.type,
                        payment_method: this.paymentMethod,
                        date: isEmpty(this.date) ? null : this.date,
                        notes: isEmpty(this.notes) ? null : this.notes,
                        extra_data: isEmpty(this.extraData) ? null : this.extraData,
                        send_to_guest: this.sendToGuest,
                    };

                    await API.post(ENDPOINT(this.reservation.id), data);
                    this.$bvModal.hide(this.modalId);
                    this.onUpdate();
                    this.successToast({ text: `${BILL_TYPE_MAPPING[this.type]} successfully added` });
                } catch (e) {
                    this.errorToast({
                        text: e.response?.data?.errors || `Error: Could not create ${BILL_TYPE_MAPPING[this.type]}`,
                    });
                } finally {
                    this.busy = false;
                }
            },
            async updateBill() {
                try {
                    this.busy = true;
                    await API.post(
                        GUEST_RESERVATION_UPDATE_BILL_ENDPOINT(this.reservation.id, this.reservation.user.id),
                        {
                            bill_pk: this.bill.id,
                            bill_type: this.type,
                            bill_date: isEmpty(this.date) ? null : format(parseISO(this.date), 'yyyy-MM-dd'),
                            sum_paid: this.sumPaid,
                            payment_method: this.paymentMethod,
                            extra_data: isEmpty(this.extraData) ? null : this.extraData,
                            notes: isEmpty(this.notes) ? null : this.notes,
                        }
                    );
                    this.$bvModal.hide(this.modalId);
                    this.onUpdate(`${BILL_TYPE_MAPPING[this.type]} successfully updated.`);
                } catch (err) {
                    const { generic } = formatResponseError(err);
                    this.errorToast({ text: generic || `Could not update ${BILL_TYPE_MAPPING[this.type]}.` });
                } finally {
                    this.busy = false;
                }
            },
        },
        watch: {
            initialValues() {
                this.reservation = this.initialValues;
            },
        },
    };
</script>

<style lang="scss" scoped>
    :deep(.form-control:focus) {
        box-shadow: none !important;
    }

    :deep(.modal-body) {
        padding: 20px !important;
    }
</style>
